import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import type {
  CreateParameterBody,
  ParameterItem,
  ParametersListNormalizedResponse,
  ParametersListParams,
  UpdateParameterBody,
} from "../../services/parameters.service";
import { parametersService } from "../../services/parameters.service";

export const parameterKeys = {
  all: ["parameters"] as const,
  lists: () => [...parameterKeys.all, "list"] as const,
  list: (params: ParametersListParams = {}) =>
    [...parameterKeys.lists(), { params }] as const,
  details: () => [...parameterKeys.all, "detail"] as const,
  detail: (id: number) => [...parameterKeys.details(), id] as const,
};

export function useParametersList(
  params: ParametersListParams = {},
  options?: Omit<
    UseQueryOptions<ParametersListNormalizedResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<ParametersListNormalizedResponse, Error>({
    queryKey: parameterKeys.list(params),
    queryFn: () => parametersService.getList(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateParameter(
  options?: UseMutationOptions<ParameterItem, Error, CreateParameterBody>
) {
  const queryClient = useQueryClient();
  return useMutation<ParameterItem, Error, CreateParameterBody>({
    mutationFn: (body) => parametersService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: parameterKeys.all });
    },
    ...options,
  });
}

export function useUpdateParameter(
  options?: UseMutationOptions<
    ParameterItem,
    Error,
    { id: number; data: UpdateParameterBody }
  >
) {
  const queryClient = useQueryClient();
  return useMutation<ParameterItem, Error, { id: number; data: UpdateParameterBody }>({
    mutationFn: ({ id, data }) => parametersService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: parameterKeys.all });
      queryClient.setQueryData(parameterKeys.detail(data.id), data);
    },
    ...options,
  });
}

export function useDeleteParameter(
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => parametersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: parameterKeys.all });
    },
    ...options,
  });
}

